import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Avatar,
    Chip,
    Collapse,
    Container,
    IconButton,
    Paper,
    Stack,
    TableCell,
    TableRow, Typography
} from '@mui/material';
import { useStoreState } from 'pullstate';
import React, { useCallback, useEffect, useState } from 'react';
import Strengths from '../../components/Strengths.jsx';
import { AppStore } from '../../stores/AppStore.js';
import { getPeopleBuilderStatus } from '../../utils/getPeopleBuilderStatus.js';
import CheckIns from '../checkins/CheckIns.jsx';
import RequiredCheckIn from '../checkins/RequiredCheckIn.jsx';
import Goals from '../goals/Goals.jsx';

const PeopleBuilderRow = React.forwardRef((props, ref) => {
    const { playerId, goals, peopleBuilder, pbStatus, setPbStatus, setTitle, title, checkIns, handleScroll, strengths } = props;
    const { employees } = useStoreState(AppStore)
    const [userPeopleBuilder] = useState(employees.find(employee => employee.id === peopleBuilder.player.id));
    const [coachPeopleBuilder] = useState(employees.find(employee => employee.full_name === userPeopleBuilder.coach));
    const [open, setOpen] = React.useState(false);
    const [userStrengths, setUserStrengths] = useState([])
    const [pbClosed, setPbClosed] = useState(false)
    const [dateCreation, setDateCreation] = useState('')
    const [rowStatus, setRowStatus] = useState({ status: '', color: '' })
    const [filterGoalsByUser, setFilterGoalsByUser] = useState([])
    const [pbData, setPbData] = useState({
        year: null,
        status: null,
        qty_goals: null,
        creating: null,
        last_checkin: null
    })

    useEffect(() => {
        if (!goals?.length || !peopleBuilder) return;
        const filterGoalsByUser = goals.filter(goal => goal?.people_builder?.id === peopleBuilder?.id && goal.goal_status !== 'archived');
        setFilterGoalsByUser(filterGoalsByUser)
    }, [goals, peopleBuilder]);

    const getDateCreation = useCallback(() => {
        if (filterGoalsByUser.length === 0) return null;
        const mostRecentGoal = filterGoalsByUser?.reduce((mostRecent, current) => {
            return new Date(current.createdAt) > new Date(mostRecent.createdAt) ? current : mostRecent;
        });

        setDateCreation(mostRecentGoal.createdAt.split('T')[0])

    }, [goals, pbStatus, checkIns, filterGoalsByUser])

    const updateStatus = useCallback(() => {
        const status = getPeopleBuilderStatus(filterGoalsByUser)
        setPbStatus(status.status)
        setRowStatus(status)
    }, [filterGoalsByUser])

    const checkPbClosed = useCallback(() => {
        // setPbClosed(new Date(peopleBuilder.pb_settings.start_date) <= new Date() && new Date(peopleBuilder.pb_settings.end_date) >= new Date())
        setPbClosed(peopleBuilder.status !== 'active')
    }, [peopleBuilder])

    const findMostRecentCheckin = (checkins) => {
        if (checkins?.length === 0) return null;

        const checinsFilteredByYear = checkins?.filter(checkin => new Date(checkin.check_in_date).getFullYear() === peopleBuilder?.year)

        if (checinsFilteredByYear?.length === 0) return null;

        return checinsFilteredByYear?.reduce((mostRecent, current) => {
            return new Date(current.check_in_date) > new Date(mostRecent.check_in_date) ? current : mostRecent;
        });
    };

    const buildData = useCallback(() => {

        const userCheckins = checkIns?.filter(checkin => checkin.player_id === playerId)
        const mostRecentCheckin = findMostRecentCheckin(userCheckins);
        getDateCreation()
        setPbData(prevStates => ({
            ...prevStates,
            qty_goals: filterGoalsByUser.length,
            status: filterGoalsByUser.length > 0 ? rowStatus.status : 'No Goals',
            creating: dateCreation,
            year: peopleBuilder?.year,
            last_checkin: mostRecentCheckin?.check_in_date.split('T')[0]
        }))
    }, [filterGoalsByUser, peopleBuilder, checkIns, dateCreation, rowStatus, playerId])

    useEffect(() => {
        buildData()
        updateStatus()
    }, [goals, checkIns, filterGoalsByUser])

    const openRow = useCallback(() => {
        setOpen(localStorage.getItem('playerIdClickedPB') === peopleBuilder?.player.id + peopleBuilder?.year)
    }, [open])

    const getUserStrengths = useCallback(async () => {
        const strengthFiltered = strengths.filter(employee => employee.user_id == playerId)
        setUserStrengths(strengthFiltered)
    }, [strengths])

    useEffect(() => {
        getUserStrengths()
    }, [])

    useEffect(() => {
        checkPbClosed()
        openRow()
    }, [])

    useEffect(() => {
        checkPbClosed()
    }, [pbClosed])

    return (
        <React.Fragment >
            <TableRow
                hover
                ref={ref}
                sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
                onClick={(e) => {
                    localStorage.setItem('playerIdClickedPB', peopleBuilder?.player.id + peopleBuilder?.year);
                    handleScroll()
                    setOpen(!open)
                }}
            >
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Stack direction='row' alignItems='center' gap={1}>
                        <Avatar src={peopleBuilder?.player.photo_url} />
                        <Typography variant='body1'>{peopleBuilder?.player.full_name}</Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Stack direction='row' alignItems='center' gap={1}>
                        {coachPeopleBuilder?.photo_url && (
                            <>
                                <Avatar src={coachPeopleBuilder?.photo_url ?? ''} />
                                <Typography variant='body1'>{coachPeopleBuilder?.full_name ?? ''}</Typography>
                            </>
                        )}
                    </Stack>
                </TableCell>
                <TableCell>{pbData.year}</TableCell>
                <TableCell>
                    <Chip sx={{ backgroundColor: rowStatus?.color || '', color: 'black' }} label={rowStatus?.status || ''} />
                </TableCell>
                <TableCell>{pbData.qty_goals}</TableCell>
                <TableCell>{pbData.creating}</TableCell>
                <TableCell>{pbData.last_checkin}</TableCell>
            </TableRow>
            <TableRow hover >
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={8}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {userStrengths.length ? (
                            <>
                                <Container maxWidth='md' sx={{ mt: 2 }}>
                                    <Strengths strengths={userStrengths} employeeId={playerId} styles={{ marginRight: 10 }} />
                                </Container>
                            </>
                        ) : ('')}
                        <Paper sx={{ m: 2, p: 2 }}>
                            <Typography
                                variant="h6"
                                textAlign='center'
                                gutterBottom
                                component="div"
                            >
                                Goals
                            </Typography>
                            <Goals pbClosed={pbClosed} peopleBuilder={peopleBuilder} playerId={playerId} goals={goals} />
                        </Paper>
                        <CheckIns pbClosed={pbClosed} peopleBuilder={peopleBuilder} goals={goals} playerId={playerId} />
                        <RequiredCheckIn pbClosed={pbClosed} setTitle={setTitle} title={title} peopleBuilder={peopleBuilder} playerId={playerId} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
})

export default PeopleBuilderRow